/**
 * @typedef {'text' | 'phone' | 'select' | 'number' | 'date' | 'textarea'} FieldType
 */

/**
 * @typedef {Object} ValidationRules
 * @property {number} [min] - Minimum value for numbers
 * @property {number} [max] - Maximum value for numbers
 * @property {number} [minLength] - Minimum length for text
 * @property {number} [maxLength] - Maximum length for text
 * @property {RegExp|string} [pattern] - Regex pattern to match
 * @property {string} [patternMessage] - Custom message for pattern failure
 */

/**
 * @typedef {Object} FieldCondition
 * @property {string} field - Field name to check
 * @property {*} value - Value to match (or array for 'in'/'notIn')
 * @property {'equals'|'notEquals'|'in'|'notIn'|'exists'} [operator='equals'] - Comparison operator
 */

/**
 * @typedef {Object} SelectOption
 * @property {string} value - Option value
 * @property {string} label - Display label
 */

/**
 * @typedef {Object} FieldConfig
 * @property {string} name - Unique field identifier
 * @property {string} label - Display label
 * @property {FieldType} type - Field type
 * @property {boolean} [required=false] - Whether field is required
 * @property {string} [placeholder] - Placeholder text
 * @property {string} [hint] - Help text displayed below the field
 * @property {*} [defaultValue] - Initial value for the field
 * @property {Array<SelectOption|string>} [options] - Options for select type
 * @property {ValidationRules} [validation] - Validation rules
 * @property {FieldCondition|function(Object): boolean} [condition] - Visibility condition
 * @property {number} [rows] - Number of rows for textarea
 */

/**
 * @typedef {Object} PlanFieldConfig
 * @property {FieldConfig[]} fields - Array of field configurations
 */

// Field type constants for custom plan fields
export const FIELD_TYPES = {
  TEXT: 'text',
  PHONE: 'phone',
  SELECT: 'select',
  NUMBER: 'number',
  DATE: 'date',
  TEXTAREA: 'textarea',
};

/**
 * @typedef {Object} OrgContextConfig
 * @property {string|null} orgGroupId - Organization group ID (null for global)
 * @property {string|null} organizationId - Organization ID (null for global/group-level)
 * @property {FieldConfig[]} fields - Array of field configurations
 */

/**
 * Configuration for each plan's custom caseSpecificData fields
 * Now supports hierarchical overrides:
 * - Global (orgGroupId: null, organizationId: null) - applies to everyone
 * - OrgGroup-level (orgGroupId: 'xxx', organizationId: null) - applies to org group
 * - Org-level (orgGroupId: 'xxx', organizationId: 'yyy') - applies to specific org
 *
 * Priority: Org-level > OrgGroup-level > Global
 *
 * @type {Object.<string, OrgContextConfig[]>}
 */
export const PLAN_FIELD_CONFIGS = {
  'Physical Therapy Initial Evaluation': [
    // Global config - applies to all orgs unless overridden
    {
      orgGroupId: 'c1c6db56-1d4c-4d4b-9bb7-64b40153d4fb',
      organizationId: null,
      fields: [
        {
          name: 'externalCaseId',
          label: 'External Case ID',
          type: FIELD_TYPES.TEXT,
          required: true,
          placeholder: 'e.g., 12345',
        },
        {
          name: 'caseName',
          label: 'Case Name',
          type: FIELD_TYPES.TEXT,
          required: true,
          placeholder: 'e.g., left knee ACL tear PO',
        },
        {
          name: 'currentPrimaryPayer',
          label: 'Primary Payer',
          type: FIELD_TYPES.TEXT,
          required: true,
          placeholder: 'e.g., Cigna',
        },
        {
          name: 'caseClinicName',
          label: 'Case Clinic Name',
          type: FIELD_TYPES.TEXT,
          required: true,
          placeholder: 'e.g., XYZ Clinic',
        },
        {
          name: 'caseDiscipline',
          label: 'Case Discipline',
          type: FIELD_TYPES.SELECT,
          required: true,
          placeholder: 'Select',
          options: [
            { value: 'Occupational Therapy', label: 'Occupational Therapy' },
            { value: 'Physical Therapy', label: 'Physical Therapy' },
          ],
        },
        {
          name: 'hasProviderReferral',
          label: 'Provider Referral',
          type: FIELD_TYPES.SELECT,
          required: true,
          placeholder: 'Select',
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
        },
        {
          name: 'hasPriorAuth',
          label: 'Prior Authorization',
          type: FIELD_TYPES.SELECT,
          required: true,
          placeholder: 'Select',
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
        },
      ],
    },
    // Example: OrgGroup-specific override (uncomment and fill in real IDs)
    // {
    //   orgGroupId: 'your-org-group-id',
    //   organizationId: null,
    //   fields: [...],
    // },
    // Example: Org-specific override (uncomment and fill in real IDs)
    // {
    //   orgGroupId: 'your-org-group-id',
    //   organizationId: 'your-org-id',
    //   fields: [...],
    // },
  ],

  // ============================================================
  // EXAMPLE: How to add a new plan with various field types
  // ============================================================
  // 'Your-Plan-Name': {
  //   fields: [
  //     // TEXT field with validation
  //     {
  //       name: 'patientId',
  //       label: 'Patient ID',
  //       type: FIELD_TYPES.TEXT,
  //       required: true,
  //       placeholder: 'e.g., P12345',
  //       hint: 'Enter the unique patient identifier',
  //       validation: { minLength: 3, maxLength: 20 },
  //     },
  //
  //     // NUMBER field with min/max
  //     {
  //       name: 'visitCount',
  //       label: 'Number of Visits',
  //       type: FIELD_TYPES.NUMBER,
  //       required: false,
  //       placeholder: 'e.g., 5',
  //       validation: { min: 1, max: 100 },
  //     },
  //
  //     // SELECT field with options
  //     {
  //       name: 'priority',
  //       label: 'Priority Level',
  //       type: FIELD_TYPES.SELECT,
  //       required: true,
  //       placeholder: 'Select priority',
  //       options: [
  //         { value: 'low', label: 'Low' },
  //         { value: 'medium', label: 'Medium' },
  //         { value: 'high', label: 'High' },
  //       ],
  //       defaultValue: 'medium',
  //     },
  //
  //     // SELECT for Yes/No (replaces deprecated YES_NO type)
  //     {
  //       name: 'hasInsurance',
  //       label: 'Has Insurance?',
  //       type: FIELD_TYPES.SELECT,
  //       required: true,
  //       options: [
  //         { value: 'yes', label: 'Yes' },
  //         { value: 'no', label: 'No' },
  //       ],
  //     },
  //
  //     // Conditional field - only shows when hasInsurance is 'yes'
  //     {
  //       name: 'insuranceDetails',
  //       label: 'Insurance Details',
  //       type: FIELD_TYPES.TEXTAREA,
  //       required: true,
  //       placeholder: 'Enter insurance information...',
  //       rows: 3,
  //       condition: { field: 'hasInsurance', value: 'yes' },
  //     },
  //
  //     // DATE field
  //     {
  //       name: 'appointmentDate',
  //       label: 'Appointment Date',
  //       type: FIELD_TYPES.DATE,
  //       required: false,
  //     },
  //
  //     // PHONE field
  //     {
  //       name: 'contactPhone',
  //       label: 'Contact Phone',
  //       type: FIELD_TYPES.PHONE,
  //       required: false,
  //       placeholder: '1234567890',
  //       hint: 'Enter 10-digit phone number',
  //     },
  //   ],
  // },
};

/**
 * Get configuration for a specific plan with hierarchical resolution
 * Priority: Org-level > OrgGroup-level > Global
 *
 * @param {string} planName - Name of the plan
 * @param {Object} [orgContext={}] - Organization context
 * @param {string} [orgContext.orgGroupId] - Organization group ID
 * @param {string} [orgContext.organizationId] - Organization ID
 * @returns {PlanFieldConfig|null} Plan configuration or null if no custom fields
 */
export const getPlanFieldConfig = (planName, orgContext = {}) => {
  const configs = PLAN_FIELD_CONFIGS[planName];
  if (!configs || !Array.isArray(configs)) return null;

  const { orgGroupId, organizationId } = orgContext;

  // Priority 1: Exact match (orgGroupId + organizationId)
  if (orgGroupId && organizationId) {
    const exactMatch = configs.find(
      (c) => c.orgGroupId === orgGroupId && c.organizationId === organizationId
    );
    if (exactMatch) return { fields: exactMatch.fields };
  }

  // Priority 2: OrgGroup match (orgGroupId only, no organizationId)
  if (orgGroupId) {
    const groupMatch = configs.find(
      (c) => c.orgGroupId === orgGroupId && !c.organizationId
    );
    if (groupMatch) return { fields: groupMatch.fields };
  }

  // Priority 3: Global fallback (both null)
  const globalConfig = configs.find((c) => !c.orgGroupId && !c.organizationId);
  return globalConfig ? { fields: globalConfig.fields } : null;
};

/**
 * Check if a plan has custom fields for the given org context
 * @param {string} planName - Name of the plan
 * @param {Object} [orgContext={}] - Organization context
 * @param {string} [orgContext.orgGroupId] - Organization group ID
 * @param {string} [orgContext.organizationId] - Organization ID
 * @returns {boolean} True if plan has custom fields
 */
export const planHasCustomFields = (planName, orgContext = {}) => {
  return !!getPlanFieldConfig(planName, orgContext);
};
